/**
 * Build a `PlotCollection.AnyEntry` from a descriptor + already-extracted
 * points, dispatching on the descriptor's payload shape.
 *
 * This is the seam the sheet pipeline rides through: a `data-series`
 * transform (formula row or legacy `_adapter`) produces `[x, value][]`,
 * and this builder types them into the right plot entry. It has no
 * sheet / DOM dependency, so the CLI/TUI render-parity work reuses it.
 * See `lona-sdk-2.0.project.docs/content/graphing-demos/rendering-stack.md`.
 */

import { type DerivedChannels } from "./derived-channels";
import type {
  BarWidthValue,
  OhlcValue,
  PlotValue,
  PointValue,
  RangeYValue,
  RecordValue,
} from "./payload-shape";
import { PlotCollection } from "./plot-collection";
import type { SeriesDescriptor } from "./series-descriptor";
import type { NumericPoint } from "../series/numeric-series";

/**
 * Construct the plot entry whose shape matches `descriptor.shape`. The
 * point values must already match that shape (scalar → number, ohlc →
 * `{open,high,low,close}`, …); they're typed at the call boundary.
 */
export function fromExtracted(
  descriptor: SeriesDescriptor,
  points: readonly NumericPoint<PlotValue>[],
  derived: Option<DerivedChannels> = null,
): PlotCollection.AnyEntry {
  switch (descriptor.shape.kind) {
    case "scalar":
      return PlotCollection.makeScalar(
        descriptor as SeriesDescriptor<"scalar">,
        points as readonly NumericPoint<number>[],
        derived,
      );
    case "bar-width":
      return PlotCollection.makeBarWidth(
        descriptor as SeriesDescriptor<"bar-width">,
        points as readonly NumericPoint<BarWidthValue>[],
        derived,
      );
    case "range-y":
      return PlotCollection.makeRangeY(
        descriptor as SeriesDescriptor<"range-y">,
        points as readonly NumericPoint<RangeYValue>[],
        derived,
      );
    case "ohlc":
      return PlotCollection.makeOhlc(
        descriptor as SeriesDescriptor<"ohlc">,
        points as readonly NumericPoint<OhlcValue>[],
        derived,
      );
    case "point":
      return PlotCollection.makePoint(
        descriptor as SeriesDescriptor<"point">,
        points as readonly NumericPoint<PointValue>[],
        derived,
      );
    case "record":
      return PlotCollection.makeRecord(
        descriptor as SeriesDescriptor<"record">,
        points as readonly NumericPoint<RecordValue>[],
        derived,
      );
  }
}
